import type {
  FinancePaymentMethod,
  ProductPaymentSettings,
  UpdateProductPaymentGatewayPayload,
} from '@/services';
import { toaster } from '@/shared/ui/toaster.component';
import { FINANCE_METHOD_LABELS } from '@/shared/utils/finance-method-label.util';
import { useStore } from '@tanstack/react-form';
import { useMemo, useState } from 'react';
import {
  gatewayMethodsError,
  toPaymentGatewayFormValues,
  toUpdatePaymentGatewayPayload,
  type PaymentGatewayFormValues,
} from '../schemas/payment-gateway.schema';
import { GATEWAY_METHOD_ORDER } from '../utils/payment-settings-labels.util';
import { catalogRowsForMethod } from '../utils/proposal-combo.util';
import {
  diffGatewaySettings,
  type SettingsChange,
} from '../utils/settings-diff.util';
import { usePaymentSettingsForm } from './use-payment-settings-form.hook';
import { useUpdatePaymentGateway } from './use-update-payment-gateway.hook';

type PendingSave = {
  payload: UpdateProductPaymentGatewayPayload;
  changes: SettingsChange[];
};

/** Campo booleano do formulário que liga cada forma do gateway. */
export const OFFER_FIELD: Record<
  FinancePaymentMethod,
  'offerPix' | 'offerCreditCard' | 'offerBoletoPix'
> = {
  PIX: 'offerPix',
  CREDIT_CARD: 'offerCreditCard',
  BOLETO_PIX: 'offerBoletoPix',
};

/**
 * Aba "Cobrança e formas": formulário, validação que cruza campos, o "de/para"
 * mostrado na confirmação e o salvamento.
 *
 * O componente é montado com `key` na versão salva: depois de salvar, a
 * consulta volta com a versão nova e o formulário renasce com os valores do
 * servidor — sem sincronizar estado à mão.
 */
export function usePaymentGatewayEditor(
  settings: ProductPaymentSettings,
  canEdit: boolean
) {
  const { gateway } = settings;
  const mutation = useUpdatePaymentGateway(settings.productCode);

  const [pending, setPending] = useState<PendingSave | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [methodsError, setMethodsError] = useState<string | undefined>();

  const savedValues = useMemo(
    () => toPaymentGatewayFormValues(gateway),
    [gateway]
  );

  const form = usePaymentSettingsForm({
    defaultValues: savedValues,
    onSubmit: ({ value }: { value: PaymentGatewayFormValues }) => {
      const crossFieldError = gatewayMethodsError(value);
      setMethodsError(crossFieldError);
      if (crossFieldError) return;

      const payload = toUpdatePaymentGatewayPayload(value, gateway.version);
      const changes = diffGatewaySettings(
        toUpdatePaymentGatewayPayload(savedValues, gateway.version),
        payload
      );

      if (changes.length === 0) {
        toaster.create({
          type: 'info',
          title: 'Nada para salvar',
          description: 'Nenhum campo foi alterado.',
        });
        return;
      }

      setActionError(null);
      setPending({ payload, changes });
    },
  });

  const values = useStore(form.store, state => state.values);

  const methodCards = GATEWAY_METHOD_ORDER.map(method => {
    const rows = catalogRowsForMethod(settings.paymentMethods, method);
    const activeRows = rows.filter(row => row.status === 'ACTIVE');
    return {
      method,
      label: FINANCE_METHOD_LABELS[method],
      field: OFFER_FIELD[method],
      isOffered: values[OFFER_FIELD[method]],
      catalogRows: activeRows.map(row => `${row.code} · ${row.description}`),
      // Ligar uma forma sem linha ativa no catálogo não quebra nada, mas o
      // corretor não vai vê-la no combo — vale avisar antes de salvar.
      missingInCatalog: activeRows.length === 0,
    };
  });

  const isLocked = !canEdit || !settings.gatewayEditable;

  const closePending = () => {
    if (mutation.isPending) return;
    setPending(null);
    setActionError(null);
  };

  const confirmPending = async () => {
    if (!pending) return;
    setActionError(null);
    try {
      await mutation.mutateAsync(pending.payload);
      toaster.create({
        type: 'success',
        title: 'Cobrança salva',
        description: 'Vale para as próximas emissões em até 5 minutos.',
      });
      setPending(null);
    } catch (error) {
      setActionError(
        error instanceof Error
          ? error.message
          : 'Não foi possível salvar a cobrança.'
      );
    }
  };

  return {
    form,
    values,
    methodCards,
    methodsError,
    clearMethodsError: () => setMethodsError(undefined),
    isLocked,
    canEnableGateway: settings.gatewaySupported && !isLocked,
    validationErrors: gateway.validationErrors,
    pending,
    actionError,
    isSaving: mutation.isPending,
    closePending,
    confirmPending,
    handleReset: () => {
      form.reset(savedValues);
      setMethodsError(undefined);
    },
  };
}
